import { defineComponent as _defineComponent } from 'vue'
import { debounce } from 'lodash-es';
import {
  computed,
  ComputedRef,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  watch,
} from 'vue';

import BrandIcon from '@/components/icons/BrandIcon.vue';
import { useRoute } from '@/composables/router/VueRouter';
import { AppPattern } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import { patternForApp } from '@/data/helpers/TaskHelper';
import { MiniApp } from '@/data/tasks/MiniApp';
import RouteNames from '@/router/RouteNames';
import { useTasksStore } from '@/stores/Tasks';
import { useTrackEntriesStore } from '@/stores/TrackEntries';
import { useTracksStore } from '@/stores/Tracks';
import { useUIStateStore } from '@/stores/UIState';

interface Props {
  // This one is required for mobile, as we show some icons in footer too
  isHeader?: boolean;
}
interface Emits {
  (e: 'clearAllNotifications'): void,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'PrimaryMenu',
  props: {
    isHeader: { type: Boolean, default: true }
  },
  emits: ["clearAllNotifications"],
  setup(__props: any) {

const props = __props as { isHeader: boolean }

const NonAppMenu = defineAsyncComponent(() => import('@/NonAppMenu.vue'));

const tracksStore = useTracksStore();
const trackEntriesStore = useTrackEntriesStore();

const tasksStore = useTasksStore();
const uiStateStore = useUIStateStore();

const route = useRoute();





const itemsHolder: Ref<HTMLDivElement | null> = ref(null);
const homeItem: Ref<HTMLDivElement | null> = ref(null);

const routeName: Ref<string> = ref('');

// TODO: on first render grab the home icon width
const itemWidth: Ref<number> = ref(69);
const maxWidth: Ref<number> = ref(0);
const itemWidthAttempt: Ref<number> = ref(0);

const menuWidthResizeObserver: Ref<ResizeObserver | null> = ref(null);

const debouncedHandleResize = debounce(handleResize, 20, {
  maxWait: 50,
  leading: true,
  trailing: true,
});

const activeAppId: ComputedRef<string | undefined> = computed(() => {
  return tracksStore.activeAppId;
});

const showMenuToggle: ComputedRef<boolean> = computed(() => {
  return ![
    '',
    RouteNames.HOME_ROUTE_NAME,
    RouteNames.PROFILE_SETTINGS_ROUTE_NAME,
    RouteNames.TASKS_ROUTE_NAME,
    RouteNames.NOTIFICATIONS_ROUTE_NAME,
    RouteNames.ICONS_ROUTE_NAME,
    RouteNames.COMPONENTS_ROUTE_NAME,
    RouteNames.TASKS_TABLE_ROUTE_NAME,
    RouteNames.APP_TABLE_ROUTE_NAME,
    RouteNames.APP_ROUTE_NAME,
    RouteNames.REPORTING_ROUTE_NAME,
    RouteNames.REPORTING_TENANT_ROUTE_NAME,
  ].includes(routeName.value);
});

const patternByApp: ComputedRef<Record<string, AppPattern>> = computed(() => {
  const patternByApp: Record<string, AppPattern> = {};
  tasksStore.tenantAppsVisibleToUser.forEach((app: MiniApp) => {
    patternByApp[app.id] = patternForApp(app);
  });
  return patternByApp;
});

const activeAppPattern: ComputedRef<AppPattern | undefined> = computed(() => {
  if (!activeAppId.value) {
    return;
  }
  return patternByApp.value[activeAppId.value];
});

const isHomeRoot: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.HOME_ROUTE_NAME;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const isMenuItemVisible: ComputedRef<boolean> = computed(() => {
  return (isMobile.value !== props.isHeader);
});

function toggleMobileSideMenu(): void {
  uiStateStore.toggleMobileSideMenuOpen();
  if (routeName.value !== RouteNames.KNOWLEDGE_MANAGEMENT_ROUTE_NAME) {
    if (trackEntriesStore.currentlyDisplayedFile) {
      trackEntriesStore.setCurrentlyDisplayedFile(null);
    }
  }
}

function handleResize(): void {
  // By requesting an animation frame as well as debouncing, we handle the edges where one debounce ends due to
  // the maxWait and the next starts
  requestAnimationFrame(() => {
    intHandleResize();
  });
}

function intHandleResize(): void {
  maxWidth.value = itemsHolder.value?.clientWidth ?? 0;
}

function updateItemWidth(): void {
  if (++itemWidthAttempt.value >= 20) {
    return;
  }
  nextTick(() => {
    const width: number = homeItem.value?.offsetWidth ?? 0;
    if (!width) {
      setTimeout(updateItemWidth, 100);
      return;
    }
    itemWidth.value = width;
  });
}

watch(() => route.name, (newValue: string) => {
  // Set via a watcher as it wasn't reliably recalculating when using directly in a getter
  routeName.value = newValue;
}, { immediate: true });

onMounted(() => {
  if (!isMobile.value) {
    nextTick(() => {
      menuWidthResizeObserver.value = new ResizeObserver(debouncedHandleResize);
      if (itemsHolder.value) {
        menuWidthResizeObserver.value.observe(itemsHolder.value);
      }
    });
    updateItemWidth();
  }
});

onBeforeUnmount(() => {
  menuWidthResizeObserver.value?.disconnect();
  menuWidthResizeObserver.value = null;
});

return { __sfc: true,NonAppMenu, tracksStore, trackEntriesStore, tasksStore, uiStateStore, route, props, itemsHolder, homeItem, routeName, itemWidth, maxWidth, itemWidthAttempt, menuWidthResizeObserver, debouncedHandleResize, activeAppId, showMenuToggle, patternByApp, activeAppPattern, isHomeRoot, isMobile, isMenuItemVisible, toggleMobileSideMenu, handleResize, intHandleResize, updateItemWidth, BrandIcon }
}

})